import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getRiderMapByIds } from '../../apis/riders';
import { usePreorderEnabled } from '../../hooks/usePreorderEnabled';
import { BillStatus } from '../../interfaces/index.types';
import { onSnapshot, orderBy, query, where } from '../../libs/firestore-wrapper';
import { getAllOrders } from '../../modals/order';
import { IOrders, OrderType } from '../../pages/Dashboard/order.types';
import { fetchMultipleSeatsByTableNumbers } from '../../utils';
import { formatSeatInfo, getSeatInformation } from '../../utils/seatHelpers';
import { useAuthContext } from '../AuthContext/AuthContext';

interface OrderStadiumContextValue {
  allOrders: IOrders[];
  preOrder: IOrders[];
  loading: boolean;
  seatDataMap: Map<string, any>;
  riderMap: Record<string, string>;
}

export type Message = { isUnread: boolean; id: string };

const OrderStadiumContext = createContext<OrderStadiumContextValue>({
  allOrders: [],
  preOrder: [],
  loading: false,
  seatDataMap: new Map(),
  riderMap: {},
});

const OrderStadiumProvider = ({ children }: { children: ReactNode }) => {
  const damagedPrefix = '_DAMAGED';
  const [loading, setLoading] = useState(true);
  const [orderList, setOrderList] = useState<IOrders[]>([]);
  const { userInfo } = useAuthContext();
  const { enabledNewVersion } = usePreorderEnabled();
  const riderMap = useRef<Record<string, string>>({});
  const [seatDataMap, setSeatDataMap] = useState<Map<string, any>>(new Map());

  const fetchData = useCallback(async () => {
    if (userInfo?.stadium_id) {
      try {
        setLoading(true);
        const ordersQuery = query(
          getAllOrders(),
          where('stadium_id', '==', userInfo.stadium_id),
          where('bill_status', '==', BillStatus.CLEARED),
          orderBy('order_create_time', 'desc')
        );
        onSnapshot(ordersQuery, async (querySnapshot: any) => {
          if (querySnapshot.empty) {
            setLoading(false);
            return;
          }
          if (querySnapshot.size) {
            const allOrders: IOrders[] = [];
            querySnapshot.forEach((doc: any) => {
              const data = doc.data() as IOrders;
              allOrders.push({
                ...data,
                id: doc.id,
                all_foods: data.foods || [],
                order_type: data.order_type || OrderType.REGULAR_ORDERS,
              });
            });

            const riderIds = allOrders
              .flatMap((order) => {
                const ids: string[] = [];
                if (order.rider_id) ids.push(order.rider_id);
                order.multi_orders?.forEach((m) => {
                  if (m.rider_id) ids.push(m.rider_id);
                });
                return ids;
              })
              .filter((id): id is string => !!id);
            const uniqueNewIds = Array.from(new Set(riderIds)).filter((id) => !riderMap.current[id]);

            if (uniqueNewIds.length > 0) {
              const newMap = await getRiderMapByIds(uniqueNewIds);
              riderMap.current = { ...riderMap.current, ...newMap };
            }
            setOrderList(allOrders);
            setLoading(false);
          }
        });
        return;
      } catch (error) {
        setLoading(false);
      }
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo?.stadium_id) {
      fetchData();
    }
  }, [userInfo]);

  const parsedOrderList = useMemo(() => {
    const getSortedOrdersWithDamaged = () => {
      const orderMap = new Map<string, IOrders[]>();

      orderList.forEach((order) => {
        const originalId = order.id.replace(new RegExp(`${damagedPrefix}(_\\d+)?$`), '');

        if (!orderMap.has(originalId)) {
          orderMap.set(originalId, []);
        }

        orderMap.get(originalId)?.push(order);
      });

      return Array.from(orderMap.values())
        .sort((a, b) => b[b.length - 1].order_create_time.seconds - a[a.length - 1].order_create_time.seconds)
        .flat();
    };
    const listAllOrder = getSortedOrdersWithDamaged();
    const allOrders = listAllOrder.filter((item) => item?.foods?.length > 0);
    const preOrder = allOrders.filter((item) => item?.is_pre_order);

    return {
      allOrders,
      preOrder,
    };
  }, [orderList]);

  useEffect(() => {
    const loadSeats = async () => {
      if (userInfo?.stadium_id && parsedOrderList.allOrders.length > 0) {
        const uniqueTableNumbers = Array.from(new Set(parsedOrderList.allOrders.map((order) => order.order_table_number).filter(Boolean)));
        const seats = await fetchMultipleSeatsByTableNumbers(userInfo.stadium_id, uniqueTableNumbers, enabledNewVersion);
        setSeatDataMap(seats);
      }
    };
    loadSeats();
  }, [parsedOrderList.allOrders, userInfo?.stadium_id, enabledNewVersion]);

  const information = (item: IOrders) => getSeatInformation(item, seatDataMap);

  const ordersWithSeatInfo = useMemo(() => {
    const populateSeatNumber = (orders: IOrders[]): IOrders[] => {
      return orders.map((order) => ({
        ...order,
        seat_number: formatSeatInfo(information(order)),
      }));
    };

    return {
      allOrders: populateSeatNumber(parsedOrderList.allOrders),
      preOrder: populateSeatNumber(parsedOrderList.preOrder),
    };
  }, [parsedOrderList, seatDataMap]);

  return (
    <OrderStadiumContext.Provider
      value={{
        loading,
        allOrders: ordersWithSeatInfo.allOrders,
        preOrder: ordersWithSeatInfo.preOrder,
        seatDataMap,
        riderMap: riderMap.current,
      }}
    >
      {children}
    </OrderStadiumContext.Provider>
  );
};

const useOrderStadiumContext = () => {
  const context = useContext(OrderStadiumContext);
  return context;
};

export { OrderStadiumProvider, useOrderStadiumContext };

